import React, { useEffect, useState } from "react";
import { getToken } from "../services/authService";
import "./PersonaList.css";

function PersonaList({ onSelectPersona, selectedPersona, refreshKey = 0 }) {
  const [usuarios, setUsuarios] = useState([]);
  const [administradores, setAdministradores] = useState([]);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showAdmins, setShowAdmins] = useState(false);

  const quitarAcentos = (s = "") =>
    s.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

  const normalizar = (s = "") => quitarAcentos(String(s).toLowerCase().trim());

  // Devuelve un string "buscable" concatenando campos útiles
  const personaIndex = (p = {}) => {
    const campos = [
      p.nombre,
      p.apellidos,
      p.email,
      p.telefono,
      p.id,
      p.nif,
      p.direccion,
      p.poblacion,
      p.provincia,
      p.cp,
      p.pais,
      p.rol,
      p.estado,
      p.notas,
      // añade aquí más claves que existan en tu API
    ];

    // Incluye además todos los valores "planos" por si hay otros campos útiles
    Object.values(p).forEach((v) => {
      if (typeof v === "string" || typeof v === "number") campos.push(v);
    });

    return normalizar(campos.filter(Boolean).join(" | "));
  };


  useEffect(() => {
    const fetchPersonas = async () => {
      try {
        const headers = { Authorization: "Bearer " + getToken() };

        const [resUsuarios, resAdmins] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL}/api/personas/sin-administrador`, { headers }),
          fetch(`${process.env.REACT_APP_API_URL}/api/personas/administradores`, { headers }),
        ]);

        if (!resUsuarios.ok || !resAdmins.ok) {
          throw new Error("Fallo en alguna de las peticiones");
        }

        const [usuariosData, adminsData] = await Promise.all([
          resUsuarios.json(),
          resAdmins.json(),
        ]);

        setUsuarios(usuariosData);
        setAdministradores(adminsData);
      } catch (err) {
        console.error("Error al cargar personas:", err);
        setError("No autorizado o error de red.");
      }
    };

    fetchPersonas();
  }, [refreshKey]);

  // Filtrado por cualquier campo, con soporte multi-palabra
  const filtrar = (lista) => {
    const tokens = normalizar(searchTerm).split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return lista;

    return lista.filter((p) => {
      const idx = personaIndex(p);
      return tokens.every((t) => idx.includes(t));
    });
  };

  const compareByNombre = (a, b) => {
    const aa = `${a.apellidos || ""}`.toLowerCase().trim();
    const ab = `${b.apellidos || ""}`.toLowerCase().trim();

    if (aa < ab) return -1;
    if (aa > ab) return 1;

    const na = `${a.nombre || ""}`.toLowerCase().trim();
    const nb = `${b.nombre || ""}`.toLowerCase().trim();

    if (na < nb) return -1;
    if (na > nb) return 1;

    return 0;
  };

  const usuariosFiltrados = filtrar(usuarios);

  const usuariosActivos = usuariosFiltrados
    .filter((p) => p.estado !== "BAJA")
    .slice()
    .sort(compareByNombre);

  const usuariosBaja = usuariosFiltrados
    .filter((p) => p.estado === "BAJA")
    .slice()
    .sort(compareByNombre);

  const renderAdmins = (lista) => (
    <>
      <h3
        className="lista-label lista-label-clickable"
        onClick={() => setShowAdmins(prev => !prev)}
      >
        Administradores {showAdmins ? "▾" : "▸"}
      </h3>

      {showAdmins && (
        lista.length === 0 ? (
          <p className="no-results">No hay administradores disponibles.</p>
        ) : (
          lista.map((persona) => (
            <div
              key={persona.id}
              className={`perfil-list-item ${selectedPersona?.id === persona.id ? "active" : ""
                }`}
              onClick={() => onSelectPersona(persona)}
              title="Administrador"
            >
              {persona.nombre} {persona.apellidos}
            </div>
          ))
        )
      )}
    </>
  );


  const renderUsuarioItem = (persona) => (
    <div
      key={persona.id}
      className={`perfil-list-item ${selectedPersona?.id === persona.id ? "active" : ""} ${persona.estado === "BAJA" ? "baja" : "activa"
        }`}
      onClick={() => onSelectPersona(persona)}
      title={persona.estado === "BAJA" ? "Usuario en baja" : "Usuario activo"}
    >
      <span>
        {persona.nombre} {persona.apellidos}
      </span>
    </div>
  );

  if (error) {
    return <p className="no-results" style={{ color: "red" }}>{error}</p>;
  }

  return (
    <div className="perfiles-list-scroll">
      <div className="search-box">
        <input
          type="text"
          placeholder="Buscar..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {renderAdmins(filtrar(administradores))}
      <div style={{ height: 20 }} />

      {/* Usuarios activos */}
      <h3 className="lista-label">Usuarios activos ({usuariosActivos.length})</h3>
      {usuariosActivos.length === 0 ? (
        <p className="no-results">No hay usuarios activos.</p>
      ) : (
        usuariosActivos.map(renderUsuarioItem)
      )}

      <div style={{ height: 12 }} />

      {/* Usuarios en baja */}
      <h3 className="lista-label">Usuarios en baja ({usuariosBaja.length})</h3>
      {usuariosBaja.length === 0 ? (
        <p className="no-results">No hay usuarios en baja.</p>
      ) : (
        usuariosBaja.map(renderUsuarioItem)
      )}
    </div>
  );
}

export default PersonaList;
